/**
 * Cover-art style painters.
 *
 * Each painter draws one visual language onto an already-primed ground. They
 * share a small context — a seeded RNG and a hue-locked colour helper — so a
 * cover is a pure function of its seed and every style stays on-palette.
 */

import { TAU } from '../lib/math.ts';

export interface CoverCtx {
  g: CanvasRenderingContext2D;
  size: number;
  /** Seeded RNG: same seed, same cover, every time. */
  rand: () => number;
  /** Colour at a hue offset, lightness and saturation multiplier. */
  hue: (offset: number, lightness: number, satMul?: number) => string;
}

export type CoverStyle = 'orbit' | 'strata' | 'prism' | 'halo' | 'grid' | 'wave';

export const COVER_STYLES: readonly CoverStyle[] = [
  'orbit',
  'strata',
  'prism',
  'halo',
  'grid',
  'wave',
];

function orbit({ g, size, rand, hue }: CoverCtx): void {
  for (let i = 0; i < 7; i++) {
    const r = size * (0.1 + i * 0.075);
    g.strokeStyle = hue(i * 9, 0.52 + i * 0.035, 1.05);
    g.globalAlpha = 0.34 + rand() * 0.3;
    g.lineWidth = size * (0.006 + rand() * 0.016);
    g.beginPath();
    const rx = r * (0.62 + rand() * 0.5);
    g.ellipse(
      size * (0.42 + rand() * 0.12),
      size * (0.46 + rand() * 0.1),
      r,
      rx,
      rand() * Math.PI,
      0,
      TAU,
    );
    g.stroke();
  }
  g.globalAlpha = 1;
  const gl = g.createRadialGradient(
    size * 0.5,
    size * 0.46,
    0,
    size * 0.5,
    size * 0.46,
    size * 0.3,
  );
  gl.addColorStop(0, hue(30, 0.82, 1.1));
  gl.addColorStop(1, `${hue(30, 0.82, 0)}00`);
  g.fillStyle = gl;
  g.beginPath();
  g.arc(size * 0.5, size * 0.46, size * 0.3, 0, TAU);
  g.fill();
}

function strata({ g, size, rand, hue }: CoverCtx): void {
  let y = size * 0.14;
  while (y < size) {
    const h = size * (0.03 + rand() * 0.1);
    g.fillStyle = hue(rand() * 46 - 23, 0.28 + rand() * 0.5, 0.9 + rand() * 0.4);
    g.globalAlpha = 0.55 + rand() * 0.4;
    g.beginPath();
    g.moveTo(0, y);
    for (let x = 0; x <= size; x += size / 6) {
      g.lineTo(x, y + Math.sin((x / size) * 5 + rand() * 3) * size * 0.03);
    }
    g.lineTo(size, y + h);
    g.lineTo(0, y + h);
    g.closePath();
    g.fill();
    y += h + size * 0.012;
  }
}

function prism({ g, size, rand, hue }: CoverCtx): void {
  for (let i = 0; i < 9; i++) {
    g.globalAlpha = 0.24 + rand() * 0.34;
    g.fillStyle = hue(i * 14 - 40, 0.4 + rand() * 0.44, 1.05);
    g.beginPath();
    const x = rand() * size;
    const y = rand() * size;
    const r = size * (0.2 + rand() * 0.35);
    const a = rand() * TAU;
    for (let k = 0; k < 3; k++) {
      g.lineTo(x + Math.cos(a + (k * TAU) / 3) * r, y + Math.sin(a + (k * TAU) / 3) * r);
    }
    g.closePath();
    g.fill();
  }
}

function halo({ g, size, rand, hue }: CoverCtx): void {
  const cx = size * 0.5;
  const cy = size * 0.52;
  const gl = g.createRadialGradient(cx, cy * 0.8, size * 0.02, cx, cy, size * 0.62);
  gl.addColorStop(0, hue(40, 0.92, 0.7));
  gl.addColorStop(0.35, hue(6, 0.56, 1.1));
  gl.addColorStop(1, hue(-40, 0.12, 1));
  g.fillStyle = gl;
  g.fillRect(0, 0, size, size);
  g.globalAlpha = 0.5;
  g.lineWidth = size * 0.0035;
  for (let i = 0; i < 46; i++) {
    const a = rand() * TAU;
    const r0 = size * (0.2 + rand() * 0.12);
    const r1 = r0 + size * (0.08 + rand() * 0.3);
    g.strokeStyle = hue(20, 0.9, 1.2);
    g.beginPath();
    g.moveTo(cx + Math.cos(a) * r0, cy + Math.sin(a) * r0);
    g.lineTo(cx + Math.cos(a) * r1, cy + Math.sin(a) * r1);
    g.stroke();
  }
}

function grid({ g, size, rand, hue }: CoverCtx): void {
  const n = 5 + Math.floor(rand() * 4);
  const cell = size / n;
  for (let i = 0; i < n; i++) {
    for (let j = 0; j < n; j++) {
      if (rand() < 0.32) continue;
      g.globalAlpha = 0.3 + rand() * 0.6;
      g.fillStyle = hue(rand() * 60 - 30, 0.3 + rand() * 0.5, 0.9 + rand() * 0.4);
      const pad = cell * rand() * 0.28;
      if (rand() < 0.4) {
        g.beginPath();
        g.arc(i * cell + cell / 2, j * cell + cell / 2, cell / 2 - pad, 0, TAU);
        g.fill();
      } else {
        g.fillRect(i * cell + pad, j * cell + pad, cell - pad * 2, cell - pad * 2);
      }
    }
  }
}

function wave({ g, size, rand, hue }: CoverCtx): void {
  for (let i = 0; i < 26; i++) {
    g.globalAlpha = 0.22 + rand() * 0.3;
    g.strokeStyle = hue(i * 4 - 30, 0.4 + (i / 26) * 0.42, 1.05);
    g.lineWidth = size * (0.004 + rand() * 0.012);
    g.beginPath();
    const amp = size * (0.04 + rand() * 0.13);
    const off = rand() * TAU;
    const base = size * (0.08 + (i / 26) * 0.84);
    for (let x = 0; x <= size; x += 6) {
      g.lineTo(x, base + Math.sin((x / size) * (2 + rand() * 0.2) * TAU + off) * amp);
    }
    g.stroke();
  }
}

export const COVER_PAINTERS: Record<CoverStyle, (c: CoverCtx) => void> = {
  orbit,
  strata,
  prism,
  halo,
  grid,
  wave,
};
